import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { API_URL } from "./config";
import { useParams, useSearchParams } from "react-router-dom";

const ORANGE = "#F07828";
const BLUE = "#1565C0";
const BORDER = "rgba(25,118,210,0.20)";
const AUTOSAVE_DELAY_MS = 1200;
const SAVE_TIMEOUT_MS = 15000;

const AILessonStudentPage = () => {
  const { lessonId } = useParams();
  const [searchParams] = useSearchParams();
  const reviewModeFromUrl = searchParams.get("mode") === "review";

  const getToken = () =>
    localStorage.getItem("auth") || localStorage.getItem("token") || "";

  const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [lesson, setLesson] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [attempt, setAttempt] = useState(null);

  const [answers, setAnswers] = useState({});
  const [xpEarned, setXpEarned] = useState(0);

  const [iframeH, setIframeH] = useState(760);
  const [assessment, setAssessment] = useState(null);
  const [htmlQuizStats, setHtmlQuizStats] = useState({ score: 0, answered: 0, total: 0 });
  const [iframeOk, setIframeOk] = useState(false);
  const [completionPctUi, setCompletionPctUi] = useState(0);

  const iframeRef = useRef(null);
  const iframeOkRef = useRef(false);
  const answersRef = useRef({});
  const attemptRef = useRef(null);
  const lessonRef = useRef(null);
  const assignRef = useRef(null);
  const xpRef = useRef(0);
  const saveTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const hasHydratedRef = useRef(false);
  const isUnmountingRef = useRef(false);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    attemptRef.current = attempt;
  }, [attempt]);

  useEffect(() => {
    lessonRef.current = lesson;
  }, [lesson]);

  useEffect(() => {
    assignRef.current = assignment;
  }, [assignment]);

  useEffect(() => {
    xpRef.current = xpEarned;
  }, [xpEarned]);

  useEffect(() => {
    iframeOkRef.current = iframeOk;
  }, [iframeOk]);

  const lessonJson = lesson?.LessonJson || {};
  const hasRenderedHtml = typeof lesson?.RenderedHtml === "string" && lesson.RenderedHtml.trim().length > 0;
  // Bridge for self-contained HTML review quizzes (QUESTIONS array format).
  // Student answers are sent to the parent and saved through authenticated APIs.
  // Capture assessed responses using stable IDs, regardless of the generated quiz layout.
  const htmlQuizBridge = `<script>
  (function () {
    const send = (message) => parent.postMessage({ ...message, uniflooHtmlQuiz: true }, '*');
    let locked = false;
    const answered = new Set();
    const quizNodes = () => [...document.querySelectorAll('[data-unifloo-question]')];
    function publish() {
      const ids = new Set(quizNodes().map(el => el.dataset.uniflooQuestion).filter(Boolean));
      send({ type: 'HTML_QUIZ_STATS', score: 0, answered: answered.size, total: ids.size });
    }
    function record(el) {
      if (locked || !el) return;
      const id = el.dataset.uniflooQuestion;
      if (!id) return;
      const answer = el.dataset.uniflooAnswer ?? el.value ?? el.textContent.trim();
      answered.add(id);
      send({ type: 'ANSWER_UPDATE', questionId: id, answer: String(answer) });
      publish();
    }
    document.addEventListener('click', function (event) {
      const el = event.target.closest('[data-unifloo-question][data-unifloo-answer]');
      if (el) record(el);
    }, true);
    document.addEventListener('change', function (event) {
      const el = event.target.closest('textarea[data-unifloo-question], input[data-unifloo-question], select[data-unifloo-question]');
      if (el) record(el);
    }, true);
    document.addEventListener('input', function (event) {
      const el = event.target.closest('textarea[data-unifloo-question], input[type="text"][data-unifloo-question]');
      if (el) record(el);
    }, true);
    window.addEventListener('message', function (event) {
      if (event.source !== parent) return;
      if (event.data?.type === 'ATTEMPT_STATE') locked = !!event.data.lockAnswers;
    });
    window.addEventListener('load', publish);
    setTimeout(publish, 200);
  })();
  <\/script>`;
  const studentHtml = hasRenderedHtml
    ? String(lesson.RenderedHtml).replace(/<\/body>/i, htmlQuizBridge + '</body>')
    : '';


  const getCurrentModes = () => {
    const submitted =
      attemptRef.current?.Status === "SUBMITTED" ||
      attemptRef.current?.Status === "GRADED";

    return {
      isSubmittedMode: submitted,
      isReviewModeNow: submitted && reviewModeFromUrl,
      isRevisionModeNow: submitted && !reviewModeFromUrl,
    };
  };

  const { isSubmittedMode, isReviewModeNow, isRevisionModeNow } = getCurrentModes();

  const isSubmitted = isSubmittedMode;
  const isReviewMode = isReviewModeNow;
  const isRevisionMode = isRevisionModeNow;

  const inputsLocked = isReviewMode;

  const postToIframe = (msg) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(msg, "*");
    } catch (_) {}
  };

  const getQuestionId = (q, si = null, qi = null, scope = "section") => {
    if (q?.questionId != null && String(q.questionId).trim() !== "") {
      return String(q.questionId);
    }
    if (scope === "assignment") return `exam_q_${qi}`;
    return `sec_${si}_q_${qi}`;
  };

  const allQuestions = (lsn, asg) => {
    const lj = lsn?.LessonJson || {};
    const map = new Map();
    // The HTML quiz is part of the graded lesson, even when no AssignmentJson exists.
    const html = String(lsn?.RenderedHtml || '');
    const match = html.match(/(?:const|let|var)\s+QUESTIONS\s*=\s*(\[[\s\S]*?\])\s*;/);
    if (match) {
      try {
        const items = JSON.parse(match[1]);
        items.forEach((item, i) => {
          if (!Array.isArray(item) || typeof item[0] !== 'string') return;
          const id = `html_quiz_${i + 1}`;
          map.set(id, { questionId: id, __qid: id, questionType: 'MCQ',
            questionText: item[0], options: item[2] || [], answer: item[1], marks: 1 });
        });
      } catch (_) { /* HTML quiz does not follow the supported format */ }
    }


    (lj.sections || []).forEach((sec, si) => {
      const sectionKey = String(sec.sectionKey || `sec${si}`);

      if (sec?.predictionQuestion) {
        const pq = sec.predictionQuestion;
        const qid = `prediction_${sectionKey}`;
        map.set(qid, {
          questionId: qid,
          __qid: qid,
          questionType: "MCQ",
          questionText: pq.question || "",
          options: Array.isArray(pq.options) ? pq.options : [],
          answer: pq.correctOption || "",
          explanation: pq.explanation || "",
          marks: 0,
        });
      }

      (sec.practice || []).forEach((q, qi) => {
        const qid = getQuestionId(q, si, qi, "section");
        map.set(qid, {
          ...q,
          questionId: qid,
          __qid: qid,
        });
      });
    });

    const aqs = asg?.AssignmentJson?.questions || lj?.assignment?.questions || [];
    aqs.forEach((q, qi) => {
      const qid = getQuestionId(q, null, qi, "assignment");
      map.set(qid, {
        ...q,
        questionId: qid,
        __qid: qid,
      });
    });

    return Array.from(map.values());
  };

  const getCorrectAnswer = (q) => {
    if (q?.answer != null) return String(q.answer);
    if (q?.correctAnswer != null) return String(q.correctAnswer);
    if (q?.answerKey != null) return String(q.answerKey);
    return "";
  };

  const normalizeAnswer = (val) => String(val || "").trim().toLowerCase();

  const buildPayload = (lsn, asg, ansMap) =>
    allQuestions(lsn, asg).map((q) => ({
      QuestionId: q.__qid || q.questionId,
      QuestionType: q.questionType,
      QuestionText: q.questionText,
      MaxScore: Number(q.marks) || 0,
      AnswerText: ansMap[q.__qid || q.questionId] || "",
      AnswerJson: null,
    }));

  const calcPct = (lsn, asg, ansMap) => {
    const qs = allQuestions(lsn, asg);
    if (!qs.length) return 0;
    const done = qs.filter((q) =>
      String(ansMap[q.__qid || q.questionId] || "").trim()
    ).length;
    return Math.round((done / qs.length) * 100);
  };

  const clearPendingAutosave = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  };

  const scheduleProgressRefresh = (ansMapOverride = null) => {
    if (progressTimerRef.current) {
      clearTimeout(progressTimerRef.current);
      progressTimerRef.current = null;
    }

    progressTimerRef.current = setTimeout(() => {
      const source = ansMapOverride || answersRef.current;
      const pct = calcPct(lessonRef.current, assignRef.current, source);
      setCompletionPctUi(pct);
    }, 100);
  };

  const syncIframeState = () => {
    if (!iframeOkRef.current) return;
    if (!attemptRef.current?.AttemptId) return;

    const { isReviewModeNow: reviewMode, isRevisionModeNow: revisionMode } = getCurrentModes();

    postToIframe({
      type: "ATTEMPT_STATE",
      attempt: attemptRef.current,
      answers: revisionMode ? {} : answersRef.current,
      reviewMode,
      revisionMode,
      showCorrectAnswers: reviewMode,
      showImmediateFeedback: revisionMode,
      noSave: revisionMode,
      lockAnswers: reviewMode,
      xp: xpRef.current,
    });
  };

  const persistProgress = async ({
    extraAnswers = [],
    xpOverride = null,
    showToast = false,
    showSavingSpinner = false,
  } = {}) => {
    const atp = attemptRef.current;
    if (!atp?.AttemptId) return false;

    const { isRevisionModeNow: revisionMode } = getCurrentModes();

    if (showSavingSpinner) setSaving(true);
    if (showToast) setError("");

    try {
      const merged = { ...answersRef.current };
      extraAnswers.forEach((a) => {
        if (a?.QuestionId) merged[a.QuestionId] = a.AnswerText || "";
      });

      answersRef.current = merged;

      if (!lessonRef.current?.RenderedHtml) {
        setAnswers({ ...merged });
      }

      const pct = calcPct(lessonRef.current, assignRef.current, merged);
      setCompletionPctUi(pct);

      if (revisionMode) {
        if (showToast && !isUnmountingRef.current) {
          setSuccess("Revision mode: answers are not saved.");
        }
        return true;
      }

      // HTML lessons keep answers in memory; only final score is persisted.
      if (lessonRef.current?.RenderedHtml) {
        if (showToast) setSuccess("HTML quiz answers are kept in memory until submission.");
        return true;
      }

      const lsn = lessonRef.current;
      const asg = assignRef.current;
      const xp = xpOverride !== null ? xpOverride : xpRef.current;

      await axios.post(
        `${API_URL}/api/ai-lesson-delivery/student/lessons/${lessonId}/save`,
        {
          AttemptId: atp.AttemptId,
          CompletionPercent: pct,
          TimeSpentSeconds: 0,
          Answers: buildPayload(lsn, asg, merged),
        },
        {
          headers: authHeader(),
          timeout: SAVE_TIMEOUT_MS,
        }
      );

      if (xp > 0) {
        try {
          await axios.post(
            `${API_URL}/api/ai-lesson-delivery/student/lessons/${lessonId}/xp`,
            { AttemptId: atp.AttemptId, XPEarned: xp },
            {
              headers: authHeader(),
              timeout: SAVE_TIMEOUT_MS,
            }
          );
        } catch (e) {
          console.warn("[Lesson] XP save failed:", e);
        }
      }

      postToIframe({ type: "SAVE_ACK" });

      if (showToast && !isUnmountingRef.current) {
        setSuccess("Progress saved.");
      }

      return true;
    } catch (err) {
      console.error("[Lesson] persistProgress error:", err.response || err);

      if (showToast && !isUnmountingRef.current) {
        setError(
          err.code === "ECONNABORTED"
            ? "Save timed out."
            : err.response?.data?.error || "Save failed."
        );
      }

      return false;
    } finally {
      if (showSavingSpinner && !isUnmountingRef.current) {
        setSaving(false);
      }
    }
  };

  const scheduleAutosave = () => {
    clearPendingAutosave();

    const atp = attemptRef.current;
    if (!atp?.AttemptId) return;

    const { isRevisionModeNow: revisionMode, isReviewModeNow: reviewMode } = getCurrentModes();
    if (revisionMode || reviewMode) return;
    if (atp?.Status === "SUBMITTED" || atp?.Status === "GRADED") return;
    if (!hasHydratedRef.current) return;

    saveTimerRef.current = setTimeout(() => {
      persistProgress({ showToast: false, showSavingSpinner: false });
    }, AUTOSAVE_DELAY_MS);
  };

  const fetchLesson = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `${API_URL}/api/ai-lesson-delivery/student/lessons/${lessonId}`,
        { headers: authHeader(), timeout: SAVE_TIMEOUT_MS }
      );

      setAssessment(res.data?.assessment || null);
      const fetchedLesson = res.data?.lesson || null;
      const fetchedAssignment = res.data?.assignment || null;
      const fetchedAttempt = res.data?.attempt || null;
      const fetchedXp = res.data?.attempt?.XPEarned || 0;

      setLesson(fetchedLesson);
      setAssignment(fetchedAssignment);
      setAttempt(fetchedAttempt);
      setXpEarned(fetchedXp);

      const submitted =
        fetchedAttempt?.Status === "SUBMITTED" ||
        fetchedAttempt?.Status === "GRADED";

      const revisionModeForThisLoad = submitted && !reviewModeFromUrl;

      const prev = {};
      if (!revisionModeForThisLoad) {
        (res.data?.answers || []).forEach((a) => {
          prev[a.QuestionId] = a.AnswerText || "";
        });
      }

      setAnswers(prev);
      answersRef.current = prev;
      attemptRef.current = fetchedAttempt;
      lessonRef.current = fetchedLesson;
      assignRef.current = fetchedAssignment;
      xpRef.current = fetchedXp;

      setCompletionPctUi(submitted ? 100 : calcPct(fetchedLesson, fetchedAssignment, prev));
      setHtmlQuizStats({ score: 0, answered: 0, total: 0 });
      hasHydratedRef.current = true;

      if (iframeOkRef.current) {
        setTimeout(() => syncIframeState(), 50);
      }
    } catch (err) {
      console.error("[Lesson] fetchLesson error:", err);
      setError(err.response?.data?.error || "Failed to load lesson.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lessonId) fetchLesson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, reviewModeFromUrl]);

  const handleStart = async () => {
    if (!getToken()) {
      setError("Not logged in.");
      return;
    }

    setStarting(true);
    setError("");

    try {
      const res = await axios.post(
        `${API_URL}/api/ai-lesson-delivery/student/lessons/${lessonId}/start`,
        {},
        { headers: authHeader(), timeout: SAVE_TIMEOUT_MS }
      );

      const newAttempt = {
        ...(attemptRef.current || {}),
        AttemptId: res.data?.AttemptId,
        Status: "IN_PROGRESS",
      };

      setAttempt(newAttempt);
      attemptRef.current = newAttempt;
      setSuccess("Lesson started!");
      syncIframeState();
    } catch (err) {
      console.error("[Lesson] start error:", err.response || err);
      setError(
        err.response?.data?.error ||
          `Start failed (${err.response?.status ?? "network error"})`
      );
    } finally {
      setStarting(false);
    }
  };

  const handleSave = async (extraAnswers = [], xpOverride = null) => {
    return await persistProgress({
      extraAnswers,
      xpOverride,
      showToast: true,
      showSavingSpinner: true,
    });
  };

  const handleSubmit = async () => {
    const atp = attemptRef.current;
    if (!atp?.AttemptId) {
      setError("Start the lesson first.");
      return;
    }

    const { isRevisionModeNow: revisionMode, isReviewModeNow: reviewMode } = getCurrentModes();
    if (revisionMode || reviewMode) {
      setError("Submission is disabled in revision mode.");
      return;
    }

    clearPendingAutosave();
    setSubmitting(true);
    setError("");

    try {
      const ok = await persistProgress({
        extraAnswers: [],
        xpOverride: xpRef.current,
        showToast: false,
        showSavingSpinner: false,
      });

      if (!ok) throw new Error("Save before submit failed");
      if (lessonRef.current?.RenderedHtml && assessment?.questions?.length && !Object.keys(answersRef.current).some(id => assessment.questions.some(q => q.id === id))) {
        throw new Error("No quiz answers captured. Please select an answer before submitting.");
      }

      await axios.post(
        `${API_URL}/api/ai-lesson-delivery/student/lessons/${lessonId}/submit`,
        {
          AttemptId: atp.AttemptId,
          CompletionPercent: 100,
          TimeSpentSeconds: 0,
          ...(lessonRef.current?.RenderedHtml ? {
            ScoreOnlyAnswers: Object.entries(answersRef.current)
              .filter(([id]) => assessment ? assessment.questions.some(q => q.id === id) : /^html_quiz_\d+$/.test(id))
              .map(([QuestionId, AnswerText]) => ({ QuestionId, AnswerText }))
          } : {}),
        },
        { headers: authHeader(), timeout: SAVE_TIMEOUT_MS }
      );

      setSuccess("Lesson submitted successfully!");
      await fetchLesson();
    } catch (err) {
      console.error("[Lesson] submit error:", err.response || err);
      setError(
        err.code === "ECONNABORTED"
          ? "Submit timed out."
          : err.response?.data?.error || err.message || "Submit failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handler = (event) => {
      const d = event.data;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!d || typeof d !== "object" || !d.type) return;

      const { isRevisionModeNow: revisionMode, isReviewModeNow: reviewMode } = getCurrentModes();

      if (d.type === "SAVE") {
        clearPendingAutosave();
        if (!revisionMode && !reviewMode) {
          handleSave(d.answers || [], d.xp ?? null);
        } else {
          setSuccess("Revision mode: answers are not saved.");
        }
      }

      if (d.type === "SUBMIT") {
        clearPendingAutosave();
        if (!revisionMode && !reviewMode && !lessonRef.current?.RenderedHtml) {
          persistProgress({
            extraAnswers: d.answers || [],
            xpOverride: d.xp ?? null,
            showToast: false,
            showSavingSpinner: false,
          }).then((ok) => {
            if (ok) handleSubmit();
          });
        } else {
          setError("Submission is disabled in revision mode.");
        }
      }

      if (d.type === "XP_UPDATE" && typeof d.xp === "number") {
        if (!revisionMode) {
          setXpEarned(d.xp);
          xpRef.current = d.xp;
        }
      }

      if (d.type === "ANSWER_UPDATE" && d.questionId && !revisionMode && !reviewMode) {
        const nextVal = d.answer ?? "";

        if (answersRef.current[d.questionId] === nextVal) return;

        const next = {
          ...answersRef.current,
          [d.questionId]: nextVal,
        };

        answersRef.current = next;
        setAnswers(next);
        scheduleProgressRefresh(next);

        if (!revisionMode && !reviewMode && !lessonRef.current?.RenderedHtml) {
          scheduleAutosave();
        }
      }

      if (d.type === "HTML_QUIZ_STATS") {
        setHtmlQuizStats({ score: Number(d.score) || 0, answered: Number(d.answered) || 0, total: Number(d.total) || 0 });
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const flushBeforeLeave = () => {
      clearPendingAutosave();

      if (!attemptRef.current?.AttemptId) return;

      const { isRevisionModeNow: revisionMode, isReviewModeNow: reviewMode } = getCurrentModes();
      if (revisionMode || reviewMode) return;

      if (
        attemptRef.current?.Status === "SUBMITTED" ||
        attemptRef.current?.Status === "GRADED"
      ) {
        return;
      }

      persistProgress({
        showToast: false,
        showSavingSpinner: false,
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushBeforeLeave();
    };

    window.addEventListener("pagehide", flushBeforeLeave);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      isUnmountingRef.current = true;
      clearPendingAutosave();

      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }

      window.removeEventListener("pagehide", flushBeforeLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onIframeLoad = () => {
    iframeOkRef.current = true;
    setIframeOk(true);
    setTimeout(() => syncIframeState(), 50);


  };

  const completionPercent = isSubmitted ? 100 : Math.max(completionPctUi,
    htmlQuizStats.total ? Math.round(htmlQuizStats.answered / htmlQuizStats.total * 100) : 0);
  const liveQuizScore = isSubmitted ? Number(attempt?.TotalScore || 0) : "Pending submission";
  const liveQuizMax = isSubmitted ? Number(attempt?.MaxScore || 0) : htmlQuizStats.total;

  const metaJson = (() => {
    try {
      return typeof lesson?.LessonMetaJson === "string"
        ? JSON.parse(lesson.LessonMetaJson)
        : lesson?.LessonMetaJson || {};
    } catch {
      return {};
    }
  })();

  const renderReviewBlock = (q) => {
    const shouldShow = isReviewMode || isRevisionMode;
    if (!shouldShow) return null;

    const studentAnswer = String(answers[q.questionId] || "").trim();
    const correctAnswer = String(getCorrectAnswer(q) || "").trim();

    if (isRevisionMode && !studentAnswer) return null;

    let isCorrect = false;
    if (studentAnswer && correctAnswer) {
      isCorrect = normalizeAnswer(studentAnswer) === normalizeAnswer(correctAnswer);
    }

    return (
      <Box
        sx={{
          mt: 1.25,
          p: 1.25,
          borderRadius: 2,
          border: `1px solid ${
            isCorrect ? "rgba(22,163,74,0.25)" : "rgba(25,118,210,0.15)"
          }`,
          backgroundColor: isCorrect
            ? "rgba(22,163,74,0.05)"
            : "rgba(25,118,210,0.03)",
        }}
      >
        <Typography sx={{ fontSize: ".82rem", fontWeight: 800, color: BLUE, mb: 0.4 }}>
          Your Answer
        </Typography>
        <Typography sx={{ fontSize: ".88rem", color: "text.secondary", mb: 1 }}>
          {studentAnswer || "No answer selected"}
        </Typography>

        <Typography sx={{ fontSize: ".82rem", fontWeight: 800, color: BLUE, mb: 0.4 }}>
          Correct Answer
        </Typography>
        <Typography
          sx={{
            fontSize: ".88rem",
            color: isCorrect ? "#15803d" : "#b45309",
            mb: q?.explanation ? 1 : 0,
          }}
        >
          {correctAnswer || "Not available"}
        </Typography>

        {q?.explanation ? (
          <>
            <Typography sx={{ fontSize: ".82rem", fontWeight: 800, color: BLUE, mb: 0.4 }}>
              Explanation
            </Typography>
            <Typography sx={{ fontSize: ".88rem", color: "text.secondary" }}>
              {q.explanation}
            </Typography>
          </>
        ) : null}
      </Box>
    );
  };

  const renderChoiceReviewOptions = (q) => {
    const studentAnswer = String(answers[q.questionId] || "").trim();
    const correctAnswer = String(getCorrectAnswer(q) || "").trim();

    return (
      <Stack spacing={1}>
        {(q.options || []).map((o, i) => {
          const optionText = String(o);
          const isSelected = normalizeAnswer(optionText) === normalizeAnswer(studentAnswer);
          const isCorrect = normalizeAnswer(optionText) === normalizeAnswer(correctAnswer);
          const isSelectedAndCorrect = isSelected && isCorrect;

          let borderColor = BORDER;
          let bg = "white";
          let textColor = "text.primary";
          let label = "";

          if (isSelectedAndCorrect) {
            borderColor = "rgba(22,163,74,0.55)";
            bg = "rgba(22,163,74,0.10)";
            textColor = "#166534";
            label = "Your answer · Correct";
          } else if (isCorrect) {
            borderColor = "rgba(22,163,74,0.45)";
            bg = "rgba(22,163,74,0.08)";
            textColor = "#166534";
            label = "Correct answer";
          } else if (isSelected) {
            borderColor = "rgba(240,120,40,0.55)";
            bg = "rgba(240,120,40,0.10)";
            textColor = "#9a3412";
            label = "Your answer";
          }

          return (
            <Box
              key={i}
              sx={{
                p: 1.15,
                borderRadius: 2,
                border: `1px solid ${borderColor}`,
                backgroundColor: bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: ".9rem",
                  fontWeight: isSelected || isCorrect ? 800 : 500,
                  color: textColor,
                }}
              >
                {optionText}
              </Typography>

              {label ? (
                <Chip
                  size="small"
                  label={label}
                  sx={{
                    height: 22,
                    fontSize: ".68rem",
                    fontWeight: 800,
                    backgroundColor: isCorrect
                      ? "rgba(22,163,74,0.14)"
                      : "rgba(240,120,40,0.14)",
                    color: isCorrect ? "#166534" : "#9a3412",
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Stack>
    );
  };

  const renderInput = (q) => {
    const set = (val) =>
      setAnswers((prev) => {
        const next = { ...prev, [q.questionId]: val };
        answersRef.current = next;
        scheduleProgressRefresh(next);

        const { isRevisionModeNow: revisionMode, isReviewModeNow: reviewMode } = getCurrentModes();
        if (!revisionMode && !reviewMode && !lessonRef.current?.RenderedHtml) {
          scheduleAutosave();
        }

        return next;
      });

    if (q.questionType === "MCQ" || q.questionType === "TRUE_FALSE") {
      return (
        <>
          {isReviewMode || isRevisionMode ? (
            <Box>
              {renderChoiceReviewOptions(q)}
              {renderReviewBlock(q)}
            </Box>
          ) : (
            <TextField
              select
              fullWidth
              size="small"
              value={answers[q.questionId] || ""}
              onChange={(e) => set(e.target.value)}
              disabled={inputsLocked}
              sx={{
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: BORDER,
                },
              }}
            >
              <MenuItem value="">— Select —</MenuItem>
              {(q.options || []).map((o, i) => (
                <MenuItem key={i} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          )}
        </>
      );
    }

    return (
      <>
        <TextField
          fullWidth
          multiline
          minRows={q.questionType === "LONG_ANSWER" ? 4 : 2}
          size="small"
          value={answers[q.questionId] || ""}
          onChange={(e) => set(e.target.value)}
          placeholder="Type your answer here..."
          disabled={inputsLocked}
          sx={{
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderColor: BORDER,
            },
          }}
        />
        {renderReviewBlock(q)}
      </>
    );
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "#F2F2F2", minHeight: "100vh" }}>
      <Paper
        elevation={5}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg,${ORANGE} 0%,#ed7f35 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: "1.05rem", lineHeight: 1.25 }}>
              {loading ? "Loading..." : lesson?.LessonTitle || "Student Lesson"}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.3, flexWrap: "wrap", alignItems: "center" }}>
              {lesson?.SubjectCode && (
                <Chip
                  size="small"
                  label={lesson.SubjectCode}
                  sx={{
                    backgroundColor: "rgba(255,255,255,.2)",
                    color: "#fff",
                    height: 20,
                    fontSize: ".7rem",
                  }}
                />
              )}
              {lesson?.ChapterTitle && (
                <Typography sx={{ fontSize: ".8rem", opacity: 0.85 }}>
                  {lesson.ChapterTitle}
                </Typography>
              )}
              {attempt?.Status && (
                <Chip
                  size="small"
                  label={attempt.Status}
                  sx={{
                    backgroundColor: isSubmitted
                      ? "rgba(34,197,94,.45)"
                      : "rgba(255,255,255,.2)",
                    color: "#fff",
                    height: 20,
                    fontSize: ".7rem",
                  }}
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            {!attempt?.AttemptId && (
              <Button
                onClick={handleStart}
                disabled={starting || loading || !lesson}
                startIcon={
                  starting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />
                }
                variant="contained"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 900,
                  backgroundColor: "rgba(255,255,255,.25)",
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(255,255,255,.35)" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {starting ? "Starting..." : "Enter Lab"}
              </Button>
            )}

            <Button
              onClick={() => handleSave()}
              disabled={saving || !attempt?.AttemptId || inputsLocked || isRevisionMode}
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 900,
                borderColor: "rgba(255,255,255,.6)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,.1)",
                },
                "&:disabled": {
                  opacity: 0.4,
                  borderColor: "rgba(255,255,255,.3)",
                  color: "rgba(255,255,255,.5)",
                },
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={submitting || !attempt?.AttemptId || inputsLocked || isRevisionMode}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} />
                ) : isSubmitted ? (
                  <CheckCircleIcon />
                ) : (
                  <SendIcon />
                )
              }
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 900,
                borderColor: isSubmitted
                  ? "rgba(34,197,94,.6)"
                  : "rgba(255,255,255,.6)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,.1)",
                },
                "&:disabled": {
                  opacity: isSubmitted ? 0.6 : 0.4,
                  borderColor: isSubmitted
                    ? "rgba(34,197,94,.4)"
                    : "rgba(255,255,255,.3)",
                  color: isSubmitted
                    ? "rgba(180,255,200,.8)"
                    : "rgba(255,255,255,.5)",
                },
              }}
            >
              {submitting ? "Submitting..." : isSubmitted ? "Submitted" : "Submit"}
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: 2, backgroundColor: "#F2F2F2" }}>
          {isRevisionMode && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              This lesson was already taken. You are now in revision mode. Answers will not be saved or submitted.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          {loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 3,
                backgroundColor: "white",
                borderRadius: 2,
                border: `1px solid ${BORDER}`,
              }}
            >
              <CircularProgress size={26} sx={{ color: ORANGE }} />
              <Typography color="text.secondary">Loading lesson...</Typography>
            </Box>
          )}

          {!loading && !lesson && (
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: 2, backgroundColor: "white", border: `1px solid ${BORDER}` }}
            >
              <Typography>Lesson not found or you don't have access.</Typography>
            </Paper>
          )}

          {!loading && lesson && (
            <Box>
              {!attempt?.AttemptId && (
                <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: "hidden", border: `1px solid ${BORDER}` }}>
                  <Box
                    sx={{
                      background: "linear-gradient(135deg,#1e3a5f 0%,#2d5a8e 100%)",
                      p: 3,
                      textAlign: "center",
                      color: "white",
                    }}
                  >
                    <Typography sx={{ fontSize: "3rem", mb: 0.75 }}>⚗️</Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: "1.35rem", mb: 0.5 }}>
                      {lessonJson?.heroBanner?.title || lesson.LessonTitle}
                    </Typography>
                    {(lessonJson?.heroBanner?.subtitle || lesson.ChapterTitle) && (
                      <Typography sx={{ opacity: 0.85, fontSize: ".9rem" }}>
                        {lessonJson?.heroBanner?.subtitle || lesson.ChapterTitle}
                      </Typography>
                    )}
                    {lessonJson?.heroBanner?.labNarrative && (
                      <Typography
                        sx={{
                          mt: 1.5,
                          opacity: 0.8,
                          fontSize: ".875rem",
                          fontStyle: "italic",
                          borderLeft: "3px solid rgba(255,255,255,.4)",
                          pl: 1.5,
                          textAlign: "left",
                          maxWidth: 600,
                          mx: "auto",
                        }}
                      >
                        {lessonJson.heroBanner.labNarrative}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ p: 2.5, backgroundColor: "white" }}>
                    {(lessonJson?.learningObjectives || []).length > 0 && (
                      <>
                        <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                          What you'll learn
                        </Typography>
                        <Stack spacing={0.625} sx={{ mb: 2 }}>
                          {lessonJson.learningObjectives.map((o, i) => (
                            <Typography key={i} sx={{ fontSize: ".875rem", color: "text.secondary" }}>
                              <span style={{ color: ORANGE, fontWeight: 700, marginRight: 6 }}>✓</span>
                              {o}
                            </Typography>
                          ))}
                        </Stack>
                      </>
                    )}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleStart}
                      disabled={starting}
                      startIcon={
                        starting ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />
                      }
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 900,
                        backgroundColor: ORANGE,
                        "&:hover": { backgroundColor: "#ed7f35" },
                        "&:disabled": { opacity: 0.65 },
                      }}
                    >
                      {starting ? "Starting lesson..." : "Enter Lab"}
                    </Button>
                  </Box>
                </Paper>
              )}

              {attempt?.AttemptId && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: `1px solid ${BORDER}`,
                    backgroundColor: "white",
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "rgba(25,118,210,0.04)",
                      borderBottom: `1px solid ${BORDER}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {isSubmitted ? (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                          <Typography sx={{ fontWeight: 900, color: "#166534", fontSize: ".85rem" }}>
                            Submitted
                          </Typography>
                          <Typography sx={{ color: "#166534", fontSize: ".85rem" }}>
                            · Score: <strong>{attempt?.TotalScore || 0}</strong> / {attempt?.MaxScore || 0}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography sx={{ fontWeight: 700, color: BLUE, fontSize: ".85rem" }}>
                          In Progress
                        </Typography>
                      )}

                      {metaJson?.sectionCount > 0 && (
                        <Chip
                          size="small"
                          label={`${metaJson.sectionCount} sections`}
                          sx={{
                            height: 20,
                            fontSize: ".72rem",
                            backgroundColor: "rgba(25,118,210,0.08)",
                            color: BLUE,
                          }}
                        />
                      )}

                      {metaJson?.estimatedMinutes > 0 && (
                        <Chip
                          size="small"
                          label={`~${metaJson.estimatedMinutes} min`}
                          sx={{
                            height: 20,
                            fontSize: ".72rem",
                            backgroundColor: "rgba(25,118,210,0.08)",
                            color: BLUE,
                          }}
                        />
                      )}

                      {xpEarned > 0 && (
                        <Chip
                          size="small"
                          icon={<FlashOnIcon style={{ color: ORANGE, fontSize: 13 }} />}
                          label={`${xpEarned} XP earned`}
                          sx={{
                            height: 20,
                            fontSize: ".72rem",
                            backgroundColor: "rgba(240,120,40,0.08)",
                            color: ORANGE,
                          }}
                        />
                      )}

                      {metaJson?.totalXP > 0 && (
                        <Typography sx={{ fontSize: ".78rem", color: "text.secondary" }}>
                          {metaJson.totalXP} XP available
                        </Typography>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      {isSubmitted &&
                        [
                          { label: "Objective", val: attempt?.ObjectiveScore || 0 },
                          { label: "Subjective", val: attempt?.SubjectiveScore || 0 },
                          {
                            label: "Total",
                            val: `${attempt?.TotalScore || 0} / ${attempt?.MaxScore || 0}`,
                          },
                        ].map(({ label, val }) => (
                          <Box key={label} sx={{ textAlign: "center" }}>
                            <Typography sx={{ fontSize: ".68rem", color: "text.secondary", lineHeight: 1.1 }}>
                              {label}
                            </Typography>
                            <Typography sx={{ fontWeight: 900, fontSize: ".85rem", color: BLUE }}>
                              {val}
                            </Typography>
                          </Box>
                        ))}
                    </Stack>
                  </Box>

                  <Box sx={{ px: 2, py: 0.875, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography sx={{ fontSize: ".78rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                      Completion
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={isSubmitted ? 100 : completionPercent}
                        sx={{
                          height: 7,
                          borderRadius: 999,
                          backgroundColor: "rgba(240,120,40,0.12)",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: isSubmitted ? "#16a34a" : ORANGE,
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: ".78rem",
                        fontWeight: 700,
                        color: isSubmitted ? "#16a34a" : ORANGE,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isSubmitted ? "100%" : `${completionPercent}%`}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {attempt?.AttemptId && hasRenderedHtml && (
                <Paper
                  elevation={0}
                  sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid ${BORDER}` }}
                >
                  {!iframeOk && (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress sx={{ color: ORANGE }} size={26} />
                      <Typography sx={{ mt: 1, color: "text.secondary", fontSize: ".875rem" }}>
                        Loading lab environment...
                      </Typography>
                    </Box>
                  )}
                  <iframe
                    ref={iframeRef}
                    srcDoc={studentHtml}
                    title={lesson.LessonTitle}
                    onLoad={onIframeLoad}
                    sandbox="allow-scripts"
                    style={{
                      width: "100%",
                      height: 'min(78vh, 900px)',
                      overflow: 'auto',
                      backgroundColor: '#e7faee',
                      border: "none",
                      display: iframeOk ? "block" : "none",
                      minHeight: 500,
                    }}
                  />
                </Paper>
              )}

              {attempt?.AttemptId && !hasRenderedHtml && metaJson?.format === "HTML" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  The generated HTML lesson could not be loaded. Check the server's
                  LESSON_HTML_DIR / UPLOAD_DIR and the lesson's saved htmlPath.
                </Alert>
              )}

              {attempt?.AttemptId && !hasRenderedHtml && metaJson?.format !== "HTML" && (
                <Stack spacing={2}>
                  {(lessonJson.sections || []).map((sec, idx) => (
                    <Paper
                      key={idx}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: `1px solid ${BORDER}`,
                        backgroundColor: idx % 2 === 0 ? "rgba(25,118,210,0.03)" : "white",
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1.05rem", mb: 1 }}>
                        {sec.icon || ""} {sec.title}
                      </Typography>

                      {sec.storyScenario && (
                        <Box
                          sx={{
                            borderLeft: `3px solid ${ORANGE}`,
                            pl: 1.5,
                            py: 0.875,
                            mb: 1.5,
                            backgroundColor: "rgba(240,120,40,0.04)",
                            borderRadius: "0 8px 8px 0",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: ".72rem",
                              color: ORANGE,
                              fontFamily: "monospace",
                              letterSpacing: 1,
                              mb: 0.25,
                            }}
                          >
                            🎬 {sec.storyScenario.setting}
                          </Typography>
                          <Typography sx={{ fontSize: ".875rem", color: "text.secondary" }}>
                            {sec.storyScenario.narrative}
                          </Typography>
                          {sec.storyScenario.hook && (
                            <Typography sx={{ fontSize: ".875rem", color: ORANGE, fontStyle: "italic" }}>
                              {sec.storyScenario.hook}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {sec.explanation && (
                        <Typography sx={{ color: "text.secondary", mb: 1.25, fontSize: ".9rem", lineHeight: 1.8 }}>
                          {sec.explanation}
                        </Typography>
                      )}

                      {(sec.deepDiscussion || []).map((p, pi) => (
                        <Typography
                          key={pi}
                          sx={{ color: "text.secondary", mb: 0.75, fontSize: ".875rem", lineHeight: 1.75 }}
                        >
                          {p}
                        </Typography>
                      ))}

                      {sec.factBanner?.text && (
                        <Box
                          sx={{
                            p: 1.5,
                            mb: 1.25,
                            borderRadius: 2,
                            border: "1px solid #FFB74D",
                            backgroundColor: "rgba(255,183,77,0.1)",
                          }}
                        >
                          <Typography sx={{ color: "#8D6E63", fontWeight: 700, fontSize: ".875rem" }}>
                            💡 {sec.factBanner.text}
                          </Typography>
                        </Box>
                      )}

                      {(sec.practice || []).length > 0 && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Typography sx={{ fontWeight: 800, color: BLUE, fontSize: ".85rem", mb: 1 }}>
                            {sec.practiceTitle || "Practice"}
                          </Typography>
                          <Stack spacing={1.25}>
                            {sec.practice.map((q, pi) => (
                              <Paper
                                key={q.questionId || pi}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: `1px solid ${BORDER}`,
                                  backgroundColor: "rgba(25,118,210,0.02)",
                                }}
                              >
                                <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: ".875rem" }}>
                                  {pi + 1}. {q.questionText}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: ".75rem",
                                    color: "text.secondary",
                                    mb: 0.75,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {q.questionType} · {q.marks || 0} mark{q.marks !== 1 ? "s" : ""}
                                </Typography>
                                {renderInput(q)}
                              </Paper>
                            ))}
                          </Stack>
                        </>
                      )}
                    </Paper>
                  ))}

                  {(() => {
                    const aqs =
                      assignment?.AssignmentJson?.questions ||
                      lessonJson?.assignment?.questions ||
                      [];
                    if (!aqs.length) return null;

                    return (
                      <Paper
                        elevation={0}
                        sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, overflow: "hidden" }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 1.25,
                            backgroundColor: "rgba(25,118,210,0.05)",
                            borderBottom: `1px solid ${BORDER}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1rem" }}>
                            📋 {assignment?.AssignmentTitle || lessonJson?.assignment?.title || "Final Assignment"}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={`${aqs.length} Questions`}
                              sx={{ height: 20, fontSize: ".72rem" }}
                            />
                            <Chip
                              size="small"
                              label={`${aqs.reduce((s, q) => s + (Number(q.marks) || 0), 0)} Marks`}
                              sx={{
                                height: 20,
                                fontSize: ".72rem",
                                backgroundColor: "rgba(240,120,40,0.1)",
                                color: ORANGE,
                              }}
                            />
                            {assignment?.durationLabel && (
                              <Typography sx={{ fontSize: ".78rem", color: "text.secondary" }}>
                                {assignment.durationLabel}
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        <Box sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            {aqs.map((q, qi) => (
                              <Paper
                                key={q.questionId || qi}
                                elevation={0}
                                sx={{ p: 2, borderRadius: 2, border: `1px solid ${BORDER}` }}
                              >
                                <Typography sx={{ fontWeight: 900, mb: 0.5, fontSize: ".9rem" }}>
                                  Q{qi + 1}. {q.questionText}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: ".75rem",
                                    color: "text.secondary",
                                    mb: 1,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {q.questionType} · {q.marks || 0} marks
                                  {q.sectionLabel ? ` · ${q.sectionLabel}` : ""}
                                </Typography>
                                {renderInput(q)}
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  })()}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AILessonStudentPage;